import React from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { Building2, Briefcase, Users, ArrowRight } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

const HowItWorks = ({ type = "recruiter" }) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const intl = useIntl();
  const prefix = type === "candidate" ? "landing_candidate" : "landing_recruiter";

  const steps = [
    {
      icon: Building2,
      title: intl.formatMessage({ id: `${prefix}.how_it_works.step1_title` }),
      description: intl.formatMessage({ id: `${prefix}.how_it_works.step1_description` }),
      cta: intl.formatMessage({ id: `${prefix}.how_it_works.step1_cta` }),
      href: type === "candidate"
        ? `/${i18nLocale}/job-offers`
        : `/${i18nLocale}/recruiters/sign_up`,
    },
    {
      icon: Briefcase,
      title: intl.formatMessage({ id: `${prefix}.how_it_works.step2_title` }),
      description: intl.formatMessage({ id: `${prefix}.how_it_works.step2_description` }),
      cta: intl.formatMessage({ id: `${prefix}.how_it_works.step2_cta` }),
      href: type === "candidate"
        ? `/${i18nLocale}/companies`
        : `/${i18nLocale}/recruiter/sign_up`,
    },
    {
      icon: Users,
      title: intl.formatMessage({ id: `${prefix}.how_it_works.step3_title` }),
      description: intl.formatMessage({ id: `${prefix}.how_it_works.step3_description` }),
      cta: intl.formatMessage({ id: `${prefix}.how_it_works.step3_cta` }),
      href: type === "candidate"
        ? `/${i18nLocale}/candidates/sign_up`
        : `/${i18nLocale}/recruiters/sign_up`,
    },
  ];
  return (
    <Box
      component="section"
      id="process"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(0, 0%, 100%)",
      }}
    >
      <Box sx={{ maxWidth: type === "candidate" ? "1250px" : "1152px", mx: "auto" }}>
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "64px" }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id={`${prefix}.how_it_works.title`} />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsl(220, 10%, 46%)",
              fontSize: "1.125rem",
              maxWidth: "36rem",
              mx: "auto",
              textAlign: "center",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id={`${prefix}.how_it_works.subtitle`} />
          </Box>
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: "24px", lg: "32px" },
          }}
        >
          {steps.map((step, i) => (
            <Box
              component={motion.div}
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              sx={{
                position: "relative",
                bgcolor: "hsl(0, 0%, 100%)",
                border: "1px solid hsl(220, 13%, 91%)",
                borderRadius: "1rem",
                p: "32px",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "hsla(145, 63%, 42%, 0.3)",
                  boxShadow: "0 10px 15px -3px hsla(145, 63%, 42%, 0.05), 0 4px 6px -4px hsla(145, 63%, 42%, 0.05)",
                  "& .step-icon-box": {
                    bgcolor: "hsla(145, 63%, 42%, 0.2)",
                  },
                  "& .step-cta": {
                    gap: "12px",
                  },
                },
              }}
            >
              {/* Icon */}
              <Box
                className="step-icon-box"
                sx={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "1rem",
                  bgcolor: "hsla(145, 63%, 42%, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "24px",
                  transition: "background-color 0.3s ease",
                }}
              >
                <step.icon size={24} color="hsl(145, 63%, 42%)" />
              </Box>

              {/* Title */}
              <Box
                component="h3"
                sx={{
                  fontSize: "1.125rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(220, 25%, 12%)",
                  mb: "12px",
                  mt: 0,
                }}
              >
                {step.title}
              </Box>

              {/* Description */}
              <Box
                component="p"
                sx={{
                  color: "hsl(220, 10%, 46%)",
                  fontSize: "0.875rem",
                  lineHeight: 1.625,
                  mb: "24px",
                  flex: 1,
                  mt: 0,
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                {step.description}
              </Box>

              {/* CTA link */}
              <Box
                component="a"
                href={step.href}
                className="step-cta"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "0.875rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(145, 63%, 42%)",
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
              >
                {step.cta} <ArrowRight size={14} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HowItWorks;
